import { PASSWORD_MIN, USERNAME_RULES } from '@chessbut/shared';
import { type FormEvent, useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { authOptions, changeUsername, logout, setPassword, startEmail, startOAuth, useSession, verifyEmailCode } from '../lib/session.ts';
import { updateSettings, useSettings } from '../lib/settings.ts';
import { openAuth } from '../lib/ui.ts';
import { Modal } from './Modal.tsx';
import { toast } from './Toast.tsx';

export function SettingsModal({ onClose }: { onClose(): void }) {
  const settings = useSettings();
  const { me } = useSession();

  const toggle = (key: keyof typeof settings, label: string) => (
    <label className="toggle">
      <input type="checkbox" checked={settings[key]} onChange={(e) => updateSettings({ [key]: e.target.checked })} />
      {label}
    </label>
  );

  return (
    <Modal title="Settings" onClose={onClose}>
      <div className="settings">
        {toggle('sound', 'Sound effects')}
        {toggle('showLegalMoves', 'Show legal moves')}
        {toggle('showHints', 'Show variant hints (snail paths, danger squares…)')}
        {toggle('coordinates', 'Board coordinates')}
        {me?.guest && (
          <div className="account-box">
            <p>
              You’re playing as <b>{me.name}</b>. Everything is saved in this browser.
            </p>
            <p className="muted small">Want your own name, or to play on your phone and laptop? Make a free account: we just email you a code.</p>
            <button
              className="btn primary block"
              onClick={() => {
                onClose();
                openAuth('signup');
              }}
            >
              Create account or sign in
            </button>
          </div>
        )}
        {me && !me.guest && <AccountSettings onClose={onClose} />}
      </div>
    </Modal>
  );
}

function AccountSettings({ onClose }: { onClose(): void }) {
  const { me } = useSession();
  const [name, setName] = useState(me?.username ?? '');
  const [busy, setBusy] = useState<string | null>(null);
  const [google, setGoogle] = useState(false);
  useEffect(() => {
    void authOptions().then((o) => setGoogle(o.google));
  }, []);
  if (!me) return null;

  const act = async (key: string, fn: () => Promise<unknown>, done?: string) => {
    setBusy(key);
    try {
      await fn();
      if (done) toast(done);
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="account-box">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void act('name', () => changeUsername(name.trim()), 'Name saved');
        }}
      >
        <label htmlFor="acct-name">Your name</label>
        <div className="join-form">
          <input id="acct-name" value={name} maxLength={20} autoCapitalize="off" spellCheck={false} onChange={(e) => setName(e.target.value)} />
          <button className="btn small" disabled={busy !== null || name.trim() === me.username || name.trim().length < 3}>
            Save
          </button>
        </div>
        <p className="hint-text">{USERNAME_RULES}</p>
      </form>
      <EmailSetting />
      <PasswordSetting />
      {google && !me.providers.includes('google') && (
        <button className="btn small" disabled={busy !== null} onClick={() => act('google', () => startOAuth('google', '/you'))}>
          Connect Google sign-in
        </button>
      )}
      {me.providers.length > 0 && <p className="muted small">Signs in with: {me.providers.map((p) => (p === 'google' ? 'Google' : 'Apple')).join(', ')}</p>}
      <div className="account-row">
        <span>
          Signed in as <b>{me.username}</b>
        </span>
        <button
          className="btn small"
          onClick={async () => {
            await logout();
            toast('Logged out');
            onClose();
          }}
        >
          Log out
        </button>
      </div>
      <DeleteAccount onDone={onClose} />
    </div>
  );
}

function DeleteAccount({ onDone }: { onDone(): void }) {
  const { me } = useSession();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  if (!me?.username) return null;
  if (!open) {
    return (
      <button className="text-link danger-link" onClick={() => setOpen(true)}>
        Delete my account
      </button>
    );
  }
  const matches = confirm.trim().toLowerCase() === me.username.toLowerCase();
  return (
    <form
      className="auth-form danger-zone"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          await api('/api/me/delete', { body: { confirm } });
          await logout();
          toast('Your account was deleted');
          onDone();
        } catch (err) {
          toast((err as Error).message);
          setBusy(false);
        }
      }}
    >
      <p className="small">
        This deletes your name, email, password, ratings, points and streak. Games you played stay in your opponents’ history as “Deleted player”. It can’t be undone.
      </p>
      <label htmlFor="delete-confirm">Type {me.username} to confirm</label>
      <input id="delete-confirm" autoCapitalize="off" spellCheck={false} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      <div className="auth-row">
        <button type="button" className="text-link" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button className="btn small danger" disabled={busy || !matches}>
          Delete account
        </button>
      </div>
    </form>
  );
}

/** Add or change the email this account signs in with (verified with a code). */
function EmailSetting() {
  const { me } = useSession();
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState('');
  const [request, setRequest] = useState<{ id: string; sentTo: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!me) return null;
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (!editing) {
    return (
      <div className="setting-line">
        <span>
          Email: {me.email ? <b>{me.email}</b> : <span className="muted">none (add one to sign in without a password)</span>}
        </span>
        <button className="btn small" onClick={() => setEditing(true)}>
          {me.email ? 'Change' : 'Add'}
        </button>
      </div>
    );
  }
  return (
    <form
      className="auth-form"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void run(async () => {
          if (!request) {
            setRequest(await startEmail(email.trim(), 'attach'));
            return;
          }
          await verifyEmailCode(request.id, code);
          toast('Email saved');
          setEditing(false);
          setRequest(null);
          setCode('');
        });
      }}
    >
      {!request ? (
        <>
          <label htmlFor="acct-email">Email</label>
          <input id="acct-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </>
      ) : (
        <>
          <label htmlFor="acct-code">Code sent to {request.sentTo}</label>
          <input id="acct-code" className="code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required />
        </>
      )}
      {error && <p className="form-error">{error}</p>}
      <div className="auth-row">
        <button type="button" className="text-link" onClick={() => { setEditing(false); setRequest(null); setError(null); }}>
          Cancel
        </button>
        <button className="btn small" disabled={busy}>
          {request ? 'Confirm' : 'Send code'}
        </button>
      </div>
    </form>
  );
}

function PasswordSetting() {
  const { me } = useSession();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!me) return null;
  if (!open) {
    return (
      <div className="setting-line">
        <span>Password: {me.hasPassword ? 'set' : <span className="muted">none (optional)</span>}</span>
        <button className="btn small" onClick={() => setOpen(true)}>
          {me.hasPassword ? 'Change' : 'Add'}
        </button>
      </div>
    );
  }
  return (
    <form
      className="auth-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await setPassword(next, me.hasPassword ? current : undefined);
          toast('Password saved');
          setOpen(false);
        } catch (err) {
          setError((err as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <input type="text" autoComplete="username" value={me.username ?? ''} readOnly hidden />
      {me.hasPassword && (
        <>
          <label htmlFor="pw-current">Current password</label>
          <input id="pw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </>
      )}
      <label htmlFor="pw-next">New password</label>
      <input id="pw-next" type="password" autoComplete="new-password" minLength={PASSWORD_MIN} value={next} onChange={(e) => setNext(e.target.value)} required />
      {error && <p className="form-error">{error}</p>}
      <div className="auth-row">
        <button type="button" className="text-link" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button className="btn small" disabled={busy}>
          Save
        </button>
      </div>
    </form>
  );
}
